import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';

import {
    buildSyncLabel,
    buildSyncTooltip,
    getSyncStatusLevel,
    parseSyncDate,
    RELATIVE_TIME_REFRESH_INTERVAL_MS,
    SHOW_SYNC_STATUS_DOT,
} from './syncStatus';

import './sync_status_indicator.scss';

/**
 * Shows how fresh the grid data is, as a colored status dot plus a relative time label
 * (e.g. "Data synchronized 2 hours ago"). The label is recomputed every minute so it stays
 * accurate without a page reload, and hovering reveals the absolute timestamp with its
 * UTC offset. Shared by every grid view that displays a last-synchronization time.
 * @param {Object} props - Component props.
 * @param {string|Date|import('moment').Moment|null} [props.value] - Raw last-sync timestamp
 *   coming from the backend; while it is missing a spinner placeholder is rendered instead.
 * @param {boolean} [props.loading] - Forces the spinner placeholder, e.g. while the view is
 *   switching to another data source and the incoming timestamp is not trustworthy yet.
 * @param {string} [props.className] - Extra class names appended to the wrapper.
 * @returns {React.ReactElement} Sync status indicator with a native hover tooltip.
 */
const SyncStatusIndicator = ({ value = null, loading = false, className = '' }) => {
    const [now, setNow] = useState(() => moment());

    useEffect(() => {
        const intervalId = setInterval(() => setNow(moment()), RELATIVE_TIME_REFRESH_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, []);

    const syncDate = useMemo(() => parseSyncDate(value), [value]);

    if (loading || !syncDate) {
        return (
            <span
                className={`sync-status-indicator sync-status-indicator--loading ${className}`.trim()}
                title="Loading synchronization status"
                aria-label="Loading synchronization status"
                role="status"
            >
                <span
                    className="spinner-border spinner-border-sm sync-status-indicator__spinner"
                    aria-hidden="true"
                />
                <span className="sync-status-indicator__label">Loading sync status</span>
            </span>
        );
    }

    const label = buildSyncLabel(syncDate, now);
    const tooltip = buildSyncTooltip(syncDate);
    const level = getSyncStatusLevel(syncDate, now);

    return (
        <span
            className={`sync-status-indicator sync-status-indicator--${level} ${className}`.trim()}
            title={tooltip}
            aria-label={tooltip}
            role="status"
        >
            {SHOW_SYNC_STATUS_DOT && (
                <span className="sync-status-indicator__dot" aria-hidden="true" />
            )}
            <span className="sync-status-indicator__label">{label}</span>
        </span>
    );
};

export default SyncStatusIndicator;
